import { useMemo, useRef, useState } from "react";
import { ScanLine, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { fmtEUR, fmtQty } from "@/lib/format";
import { inputCls } from "@/components/app/common";

const norm = (s) => (s || "").toLowerCase().trim();

export function StockBadge({ value, testId }) {
  const cls = value < 0 ? "text-[#B4553D] font-semibold" : value === 0 ? "text-[#A2513C]" : "text-[#2C2623]";
  return <span data-testid={testId} className={`tabular ${cls}`}>{fmtQty(value)} pz</span>;
}

function SelectedCard({ p, isAdmin, onClear }) {
  return (
    <div data-testid="product-picker-selected" className="flex items-start justify-between gap-3 rounded-xl border border-[#EAE3DC] bg-[#FAF7F5] px-4 py-3">
      <div className="min-w-0">
        <div className="font-medium truncate" data-testid="product-picker-selected-name">{p.nome}</div>
        <div className="text-xs text-[#756B64] mt-0.5 flex flex-wrap gap-x-3">
          <span className="tabular" data-testid="product-picker-selected-sku">{p.sku || "senza SKU"}</span>
          <span>Giacenza: <StockBadge value={p.giacenza} testId="product-picker-selected-stock" /></span>
          {isAdmin && p.costo_medio != null && <span data-testid="product-picker-selected-cost">Costo medio: <b className="tabular text-[#2C2623]">{fmtEUR(p.costo_medio)}</b></span>}
        </div>
      </div>
      <button type="button" onClick={onClear} data-testid="product-picker-clear" className="p-1.5 rounded-lg hover:bg-[#EDE5DE] text-[#756B64]" title="Cambia prodotto"><X className="h-4 w-4" /></button>
    </div>
  );
}

export function ProductPicker({ prodotti, value, onChange, isAdmin }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const ref = useRef();
  const sel = prodotti.find((p) => p.id === value);
  const matches = useMemo(() => {
    const t = norm(q);
    return (t ? prodotti.filter((p) => norm(p.sku).includes(t) || norm(p.nome).includes(t)) : prodotti).slice(0, 8);
  }, [q, prodotti]);
  const pick = (p) => { onChange(p.id); setQ(""); setOpen(false); };
  const type = (v) => {
    setQ(v); setOpen(true); setHi(0);
    const exact = prodotti.find((p) => p.sku && norm(p.sku) === norm(v));
    if (exact) pick(exact);
  };
  const key = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setHi((h) => Math.min(h + 1, matches.length - 1)); }
    if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
    if (e.key === "Enter") { e.preventDefault(); if (matches[hi]) pick(matches[hi]); }
    if (e.key === "Escape") setOpen(false);
  };
  if (sel) return <SelectedCard p={sel} isAdmin={isAdmin} onClear={() => { onChange(""); setTimeout(() => ref.current?.focus(), 0); }} />;
  return (
    <div className="relative">
      <ScanLine className="h-4 w-4 absolute left-3 top-3 text-[#8C6D62]" />
      <Input ref={ref} data-testid="product-picker-input" autoComplete="off" value={q} placeholder="Scansiona o digita SKU, oppure cerca per nome" onChange={(e) => type(e.target.value)} onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)} onKeyDown={key} className={`${inputCls} pl-9`} />
      {open && (
        <ul data-testid="product-picker-list" className="absolute z-50 mt-1 w-full max-h-64 overflow-y-auto rounded-xl border border-[#EAE3DC] bg-white shadow-lg py-1">
          {!matches.length ? <li className="px-4 py-3 text-sm text-[#756B64]" data-testid="product-picker-empty">Nessun prodotto trovato</li> : matches.map((p, i) => (
            <li key={p.id}>
              <button type="button" data-testid={`product-picker-option-${p.sku || p.id}`} onMouseDown={(e) => { e.preventDefault(); pick(p); }} onMouseEnter={() => setHi(i)}
                className={`w-full text-left px-4 py-2 flex items-center justify-between gap-3 text-sm transition-colors ${i === hi ? "bg-[#F4EFEA]" : ""}`}>
                <span className="min-w-0"><span className="block truncate">{p.nome}</span><span className="text-[11px] text-[#756B64] tabular">{p.sku || "senza SKU"}</span></span>
                <StockBadge value={p.giacenza} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
